import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formFillingApi } from '../utils/api';
import { useToast } from '../hooks/useToast';

function LogBox({ logs }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [logs]);
  return (
    <div ref={ref} style={{
      background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
      padding: 12, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)',
      height: 160, overflowY: 'auto', marginTop: 10,
    }}>
      {logs.length === 0
        ? <span style={{ color: 'var(--text-muted)' }}>Starting...</span>
        : logs.map((l, i) => <div key={i}>{l}</div>)}
    </div>
  );
}

export default function FormFilling() {
  const toast = useToast();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeJob, setActiveJob] = useState(null); // { pitchId, jobId, logs, status, result }
  const pollRef = useRef(null);

  const loadQueue = useCallback(async () => {
    try {
      const res = await formFillingApi.queue();
      setQueue(res.data);
    } catch { toast('Could not load queue', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const handleFill = async (pitch) => {
    try {
      const res = await formFillingApi.fill(pitch.id);
      const jobId = res.data.jobId;
      setActiveJob({ pitchId: pitch.id, jobId, logs: [], status: 'running', result: null, podcastName: pitch.podcastName });

      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const r = await formFillingApi.status(jobId);
          setActiveJob(prev => prev?.jobId === jobId ? { ...prev, logs: r.data.logs || [], status: r.data.status, result: r.data.result, error: r.data.error } : prev);

          if (r.data.status === 'done') {
            stopPolling();
          } else if (r.data.status === 'error') {
            stopPolling();
            toast(`Failed: ${r.data.error}`, 'error');
          }
        } catch {}
      }, 2000);
    } catch (e) {
      toast(e.response?.data?.error || 'Could not start form fill', 'error');
    }
  };

  const grouped = queue.reduce((acc, p) => {
    const key = p.clientName || p.clientId;
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  const s = {
    card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24, marginBottom: 16 },
  };

  return (
    <div style={{ padding: 32, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, marginBottom: 6 }}>Form Filling</h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
          Podcasts that sent a guest intake form. Claude fills each form using the client's form profile and the pitch that was sent — you review and submit it yourself.
        </p>
      </div>

      {/* Active job */}
      {activeJob && (
        <div style={{ ...s.card, borderColor: activeJob.status === 'error' ? 'rgba(239,68,68,0.3)' : activeJob.status === 'done' ? 'rgba(245,158,11,0.3)' : 'var(--border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            {activeJob.status === 'running' && (
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', animation: 'pulse 1.5s infinite', flexShrink: 0 }} />
            )}
            {activeJob.status === 'done' && <span style={{ color: 'var(--green)' }}>✓</span>}
            {activeJob.status === 'error' && <span style={{ color: 'var(--red)' }}>✕</span>}
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              {activeJob.status === 'running' ? `Filling form — ${activeJob.podcastName}...` :
               activeJob.status === 'done' ? `Done — ${activeJob.podcastName}` :
               `Failed — ${activeJob.podcastName}`}
            </span>
            {(activeJob.status === 'done' || activeJob.status === 'error') && (
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setActiveJob(null)}>Dismiss</button>
            )}
          </div>
          {activeJob.status === 'done' && activeJob.result?.phase === 'awaiting_review' && (
            <div style={{ marginTop: 10, background: 'var(--amber-soft)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 'var(--radius-sm)', padding: '12px 14px' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--amber)', marginBottom: 6 }}>
                {activeJob.result.fieldsFilled} field{activeJob.result.fieldsFilled !== 1 ? 's' : ''} filled — review the browser window, then submit the form yourself.
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                Once you've submitted it, click below to mark it as done.
              </div>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  await formFillingApi.markSubmitted(activeJob.result.pitchId);
                  setQueue(prev => prev.filter(p => p.id !== activeJob.result.pitchId));
                  setActiveJob(null);
                  toast(`Marked as submitted ✓`, 'success');
                } catch { toast('Could not update status', 'error'); }
              }}>
                I've Submitted the Form
              </button>
            </div>
          )}
          {activeJob.status === 'error' && (
            <div style={{ fontSize: 13, color: 'var(--red)', marginTop: 8 }}>{activeJob.error}</div>
          )}
          <LogBox logs={activeJob.logs} />
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
      ) : queue.length === 0 ? (
        <div style={s.card}>
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
            No forms in queue. When a podcast sends you an intake form, set its status to "Form" on the Podcasts page and paste the URL.
          </div>
        </div>
      ) : (
        Object.entries(grouped).map(([clientName, pitches]) => (
          <div key={clientName} style={s.card}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
              {clientName}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {pitches.map(pitch => {
                const isActive = activeJob?.pitchId === pitch.id && activeJob.status === 'running';
                return (
                  <div key={pitch.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    background: 'var(--bg)', borderRadius: 'var(--radius-sm)', padding: '12px 14px',
                    border: '1px solid var(--border)',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{pitch.podcastName}</div>
                      {pitch.formEmail && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          Submit as: {pitch.formEmail}
                        </div>
                      )}
                      {pitch.formUrl && (
                        <a href={pitch.formUrl} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 400 }}>
                          {pitch.formUrl}
                        </a>
                      )}
                    </div>
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={isActive || (activeJob?.status === 'running')}
                      onClick={() => handleFill(pitch)}
                    >
                      {isActive ? 'Filling...' : 'Fill Form'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
